import { useMemo, useState } from 'react';

import { Plus, Trash2 } from 'lucide-react';

import { TagBadge } from '@/components/contacts/TagBadge';
import { Button } from '@/components/ui/button';
import { useSetting } from '@/hooks/useSettings';
import { useTagDefinitions } from '@/hooks/useTagDefinitions';
import { useUsers } from '@/hooks/useUsers';
import {
  CHANNEL_OPTIONS,
  FILTER_DEFAULTS,
  parseDefaultFilters,
  type OwnerFilter,
  type SavedFilters,
} from '@/pages/contacts/filters';

import { parseTagGroups, type TagGroup } from '@repo/shared-types';

const FIELD_CLASS =
  'focus:ring-primary w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none';

const SWATCHES = [
  '#00a884',
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#ef4444',
  '#f59e0b',
  '#10b981',
  '#6b7b8d',
];

/** One type row: color + type name + its tags as removable chips with an inline adder. */
const TagGroupRow = ({
  group,
  onChange,
  onRemove,
}: {
  group: TagGroup;
  onChange: (group: TagGroup) => void;
  onRemove: () => void;
}) => {
  const [draft, setDraft] = useState('');

  const addTags = (text: string) => {
    const names = text
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    if (names.length === 0) return;
    const tags = [...group.tags];
    for (const name of names) {
      if (!tags.includes(name)) tags.push(name);
    }
    onChange({ ...group, tags });
  };

  return (
    <div className="flex items-start gap-2 rounded-md border p-2">
      <input
        type="color"
        value={group.color || '#6b7b8d'}
        onChange={(e) => onChange({ ...group, color: e.target.value })}
        className="mt-0.5 h-9 w-10 shrink-0 cursor-pointer rounded border p-1"
        title="Color shared by every tag of this type"
      />
      <input
        className="focus:ring-primary w-36 shrink-0 rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
        value={group.type}
        onChange={(e) => onChange({ ...group, type: e.target.value })}
        placeholder="Type — e.g. source"
      />
      <div className="flex min-h-9 min-w-0 flex-1 flex-wrap items-center gap-1 rounded-md border px-2 py-1.5">
        {group.tags.map(
          (tag) =>
            tag.trim() && (
              <TagBadge
                key={tag}
                name={tag.trim()}
                definition={{ name: tag.trim(), type: group.type, color: group.color }}
                onRemove={() => onChange({ ...group, tags: group.tags.filter((t) => t !== tag) })}
              />
            ),
        )}
        <input
          className="h-6 min-w-32 flex-1 border-0 bg-transparent text-sm focus:outline-none"
          value={draft}
          onChange={(e) => {
            // Typing a comma commits the tag immediately, like a chip input.
            if (e.target.value.includes(',')) {
              addTags(e.target.value);
              setDraft('');
            } else {
              setDraft(e.target.value);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addTags(draft);
              setDraft('');
            }
          }}
          onBlur={() => {
            if (draft.trim()) {
              addTags(draft);
              setDraft('');
            }
          }}
          placeholder={
            group.tags.length > 0 ? 'Add tag…' : 'Tags — e.g. dealerkit, autoconvert, website'
          }
        />
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="text-destructive hover:text-destructive mt-0.5 h-8 w-8 shrink-0 p-0"
        onClick={onRemove}
        title="Remove this type and all its tags"
      >
        <Trash2 size={14} />
      </Button>
    </div>
  );
};

/**
 * Visual editor for the CONTACT_TAG_DEFINITIONS setting. Type-first: each row
 * is a type with one color and the list of tags under it. All tags of a type
 * share its color, and a contact holds at most one of them at a time.
 */
export const TagDefinitionsEditor = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  const groups = useMemo(() => parseTagGroups(value), [value]);

  const write = (next: TagGroup[]) => onChange(JSON.stringify(next));

  const addGroup = () =>
    write([...groups, { type: '', color: SWATCHES[groups.length % SWATCHES.length], tags: [] }]);

  const previewGroups = groups.filter((g) => g.tags.some((t) => t.trim()));

  return (
    <div className="space-y-2">
      {groups.length === 0 && (
        <p className="text-muted-foreground text-sm italic">
          No tag types yet. Click &quot;Add type&quot;, name it (e.g. <b>source</b>), pick its
          color, then list its tags (e.g. dealerkit, autoconvert, website). All tags of a type share
          its color, and a contact can hold only one of them — adding another replaces it.
        </p>
      )}
      {groups.map((group, index) => (
        <TagGroupRow
          key={index}
          group={group}
          onChange={(next) => write(groups.map((g, i) => (i === index ? next : g)))}
          onRemove={() => write(groups.filter((_, i) => i !== index))}
        />
      ))}
      <Button type="button" variant="outline" size="sm" className="gap-1" onClick={addGroup}>
        <Plus size={14} />
        Add type
      </Button>
      {previewGroups.length > 0 && (
        <div className="mt-2 space-y-2 rounded-md border p-3">
          <p className="text-muted-foreground text-xs font-medium">
            Preview — how these tags will look on contacts
          </p>
          {previewGroups.map((group, index) => (
            <div key={index} className="flex flex-wrap items-center gap-1.5">
              <span className="text-muted-foreground w-24 shrink-0 truncate text-[11px]">
                {group.type.trim() ? `${group.type.trim()} (pick one):` : 'no type:'}
              </span>
              {group.tags.map(
                (tag) =>
                  tag.trim() && (
                    <TagBadge
                      key={tag}
                      name={tag.trim()}
                      definition={{ name: tag.trim(), type: group.type, color: group.color }}
                    />
                  ),
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Visual editor for the SALES_CONSOLE_DEFAULT_FILTERS setting. These defaults
 * only seed a user's very first visit — after that their own saved filters win.
 */
export const DefaultFiltersEditor = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  const { data: usersData } = useUsers({ limit: 100 });
  const users = usersData?.data ?? [];
  const { definitions: tagDefinitions } = useTagDefinitions();
  const filters = useMemo(() => parseDefaultFilters(value) ?? FILTER_DEFAULTS, [value]);
  const { data: intentLabelSetting } = useSetting('SALES_CONSOLE_INTENT_LABEL');
  const intentLabel = intentLabelSetting?.value?.trim() || 'High intent';

  const write = (patch: Partial<SavedFilters>) =>
    onChange(JSON.stringify({ ...filters, ...patch }));

  // One default pick per tag type, mirroring the console's per-type filters.
  const typedGroups = useMemo(() => {
    const byType = new Map<string, string[]>();
    for (const def of tagDefinitions) {
      const type = def.type?.trim();
      if (!type) continue;
      byType.set(type, [...(byType.get(type) ?? []), def.name]);
    }
    return [...byType.entries()];
  }, [tagDefinitions]);

  const selectTagForGroup = (groupTags: string[], picked: string) =>
    write({
      tagFilters: [
        ...filters.tagFilters.filter((t) => !groupTags.includes(t)),
        ...(picked ? [picked] : []),
      ],
    });

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <label className="space-y-1 text-sm">
        <span className="text-muted-foreground text-xs font-medium">List</span>
        <select
          className={FIELD_CLASS}
          value={filters.ownerFilter}
          onChange={(e) => write({ ownerFilter: e.target.value as OwnerFilter })}
        >
          <option value="active">Active (inbound sessions)</option>
          <option value="all">All contacts</option>
          <option value="unclaimed">Unclaimed (no one assigned)</option>
        </select>
      </label>
      <label className="space-y-1 text-sm">
        <span className="text-muted-foreground text-xs font-medium">Channel</span>
        <select
          className={FIELD_CLASS}
          value={filters.channelFilter}
          onChange={(e) =>
            write({ channelFilter: e.target.value as SavedFilters['channelFilter'] })
          }
        >
          {CHANNEL_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
      <label className="space-y-1 text-sm">
        <span className="text-muted-foreground text-xs font-medium">{intentLabel} filter</span>
        <select
          className={FIELD_CLASS}
          value={filters.activeTools ? 'true' : 'false'}
          onChange={(e) => write({ activeTools: e.target.value === 'true' })}
        >
          <option value="false">Off</option>
          <option value="true">On — only chats where a watched tool fired recently</option>
        </select>
      </label>
      <label className="space-y-1 text-sm">
        <span className="text-muted-foreground text-xs font-medium">Assignee</span>
        <select
          className={FIELD_CLASS}
          value={filters.assigneeFilter}
          onChange={(e) => write({ assigneeFilter: e.target.value })}
        >
          <option value="">Anyone</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.name || user.email}
            </option>
          ))}
        </select>
      </label>
      {typedGroups.map(([type, groupTags]) => {
        const selected = filters.tagFilters.find((t) => groupTags.includes(t)) ?? '';
        return (
          <label key={type} className="space-y-1 text-sm">
            <span className="text-muted-foreground text-xs font-medium capitalize">{type}</span>
            <select
              className={FIELD_CLASS}
              value={selected}
              onChange={(e) => selectTagForGroup(groupTags, e.target.value)}
            >
              <option value="">{type}: any</option>
              {groupTags.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          </label>
        );
      })}
    </div>
  );
};
